import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { saveAs } from "file-saver";
import Lf23Loader from "../../components/Lf23Loader";
import MappaArchiQgis, { COLORI } from "./MappaArchiQgis";
import { API_URL, authHeaders, nf, KpiCard, ico, formatData, ExportBtn } from "./comuni";

/**
 * Anagrafiche > Geometrie tratte: validazione delle geometrie ricavate dagli archi QGIS
 * del cliente (inizio, fine e disegno di ogni tratta).
 *
 * Il sistema abbina da solo gli archi alle tratte (nome via, giorni, metri) e controlla
 * il risultato: le tratte "OK" hanno passato tutti i controlli, quelle "Da verificare"
 * hanno un motivo scritto. Il cliente le guarda sulla mappa e le conferma; dove serve
 * corregge gli archi a mano (clic sugli archi) o inverte inizio e fine. Tutto resta nel
 * nostro DB: niente viene scritto su Interacta. Il filtro "Archi senza tratta" elenca gli
 * archi del file che nessuna tratta ha preso, per trovarli sulla mappa.
 *
 * API: /api/gestione-percorsi/geometrie-qgis (stesso permesso della pagina).
 */
const BASE = `${API_URL}/gestione-percorsi/geometrie-qgis`;

const FILTRI_STATO = [
    { value: "", label: "Tutte" },
    { value: "da_verificare", label: "Da verificare" },
    { value: "ok", label: "OK, da confermare" },
    { value: "confermate", label: "Confermate" },
    { value: "non_abbinata", label: "Non abbinate" },
    { value: "liberi", label: "Archi senza tratta" },   // l'elenco mostra archi, non tratte
];

const BADGE = {
    ok: ["badge-success", "OK"],
    da_verificare: ["badge-warning", "Da verificare"],
    non_abbinata: ["badge-neutral", "Non abbinata"],
    non_calcolata: ["badge-neutral", "Non calcolata"],
};

const BadgeStato = ({ t }) => {
    if (t.confermataIl) return <span className="badge badge-info" title={`Confermata il ${formatData(String(t.confermataIl).slice(0, 10))}`}>Confermata</span>;
    const [cls, label] = BADGE[t.stato] || BADGE.non_calcolata;
    return <span className={`badge ${cls}`}>{label}</span>;
};

const scartoTxt = (t) => {
    if (t.metri == null || !t.metriRegistro) return null;
    const p = Math.round(((t.metri - t.metriRegistro) / t.metriRegistro) * 100);
    // Sotto l'1% la percentuale direbbe "0%" anche se i metri non tornano: meglio i metri.
    const m = Math.round(t.metri - t.metriRegistro);
    if (p === 0 && m !== 0) return `${m > 0 ? "+" : ""}${m} m`;
    return `${p > 0 ? "+" : ""}${p}%`;
};

// Elenco del filtro "Archi senza tratta": stesse righe delle tratte, con codice arco e giorni.
const ElencoArchiLiberi = ({ archi, scelto, onScegli }) => (
    <>
        {archi.length === 0 && <div className="gp-geo-vuoto">Nessun arco senza tratta con questi filtri.</div>}
        {archi.map((a) => (
            <button key={a.id} className={`gp-geo-riga ${a.id === scelto ? "attiva" : ""}`} onClick={() => onScegli(a)}>
                <div className="gp-geo-riga-testa">
                    <span className="gp-geo-via">{a.street}</span>
                    <span className="badge badge-danger">Senza tratta</span>
                </div>
                <div className="gp-geo-riga-sub">
                    {a.codarco ? `Arco ${a.codarco}` : "Arco senza codice"} · {a.firma.join(", ") || "—"} · {nf(Math.round(a.lunghezza))} m
                </div>
            </button>
        ))}
    </>
);

// `zona`: ogni zona ha il suo file archi e le sue tratte; tutte le chiamate la portano in query.
const TabGeometrieTratte = ({ zona }) => {
    const [dati, setDati] = useState(null);
    const [archi, setArchi] = useState([]);
    const [loading, setLoading] = useState(true);
    const [errore, setErrore] = useState("");
    const [lavoro, setLavoro] = useState("");          // azione in corso (testo del bottone)
    const [cerca, setCerca] = useState("");
    const [fStato, setFStato] = useState("da_verificare");
    const [fGiorno, setFGiorno] = useState("");
    const [sel, setSel] = useState(null);              // codice tratta scelta
    const [fitKey, setFitKey] = useState(0);
    const [modifica, setModifica] = useState(null);    // Set degli archi in modifica, o null
    const [confermaBlocco, setConfermaBlocco] = useState(false);
    const [arcoAcceso, setArcoAcceso] = useState(null); // id arco evidenziato (codice nel pannello <-> mappa)
    const [arcoLibero, setArcoLibero] = useState(null); // id arco senza tratta scelto nell'elenco
    const [inquadra, setInquadra] = useState(null);     // archi da inquadrare in mappa (array nuovo = nuovo inquadramento)

    const carica = useCallback(async ({ conArchi = true } = {}) => {
        setErrore("");
        try {
            const [e, a] = await Promise.all([
                axios.get(BASE, { headers: authHeaders(), params: { zona } }),
                conArchi ? axios.get(`${BASE}/archi`, { headers: authHeaders(), params: { zona } }) : Promise.resolve(null),
            ]);
            setDati(e.data);
            if (a) setArchi(a.data.archi || []);
        } catch (err) {
            setErrore(err?.response?.data?.error || "Impossibile leggere le geometrie delle tratte");
        } finally {
            setLoading(false);
        }
    }, [zona]);

    useEffect(() => { carica(); }, [carica]);

    /* Dopo un'azione: elenco e legami arco->tratta si rileggono (gli archi stessi non
       cambiano, ma la loro tratta si'). */
    const ricarica = useCallback(async () => {
        const [e, a] = await Promise.all([
            axios.get(BASE, { headers: authHeaders(), params: { zona } }),
            axios.get(`${BASE}/archi`, { headers: authHeaders(), params: { zona } }),
        ]);
        setDati(e.data);
        setArchi(a.data.archi || []);
    }, [zona]);

    const esegui = async (testo, fn) => {
        setLavoro(testo);
        setErrore("");
        try {
            await fn();
            await ricarica();
        } catch (err) {
            setErrore(err?.response?.data?.error || `${testo}: operazione non riuscita`);
        } finally {
            setLavoro("");
        }
    };

    const tratte = useMemo(() => dati?.tratte || [], [dati]);
    const perCodice = useMemo(() => new Map(tratte.map((t) => [t.codice, t])), [tratte]);
    const trattaSel = sel ? perCodice.get(sel) : null;
    const perId = useMemo(() => new Map(archi.map((a) => [a.id, a])), [archi]);

    /* Una tratta confermata conta solo fra le confermate, anche se il suo stato e' rimasto
       "da verificare" (il cliente puo' confermarla nonostante l'avviso): stessa regola del
       filtro dell'elenco, altrimenti i riquadri contano tratte che l'elenco non mostra. */
    const conteggi = useMemo(() => {
        const c = { ok: 0, da_verificare: 0, non_abbinata: 0, confermate: 0 };
        for (const t of tratte) {
            if (t.confermataIl) c.confermate += 1;
            else if (c[t.stato] != null) c[t.stato] += 1;
        }
        return c;
    }, [tratte]);

    const giorni = useMemo(() => [...new Set(tratte.flatMap((t) => t.percorsi))].sort(), [tratte]);

    const vistaLiberi = fStato === "liberi";

    const filtrate = useMemo(() => {
        if (fStato === "liberi") return [];   // l'elenco mostra gli archi senza tratta
        const q = cerca.trim().toLowerCase();
        return tratte.filter((t) => {
            if (fStato === "confermate" && !t.confermataIl) return false;
            if (fStato && fStato !== "confermate" && (t.confermataIl || t.stato !== fStato)) return false;
            if (fGiorno && !t.percorsi.includes(fGiorno)) return false;
            if (q && !`${t.codice} ${t.strada} ${t.tratto}`.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [tratte, cerca, fStato, fGiorno]);

    /* Archi senza tratta, con la stessa ricerca (codice arco o via) e lo stesso filtro per
       giorno delle tratte. Sono gli stessi che il riquadro conta: nessun legame con una tratta. */
    const liberi = useMemo(() => {
        const q = cerca.trim().toLowerCase();
        return archi.filter((a) => {
            if (a.tratta) return false;
            if (fGiorno && !a.firma.includes(fGiorno)) return false;
            if (q && !`${a.codarco || ""} ${a.street}`.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [archi, cerca, fGiorno]);
    // L'arco scelto resta acceso finche' e' in elenco (dopo un salvataggio puo' avere una tratta).
    const arcoLiberoSel = vistaLiberi && liberi.some((a) => a.id === arcoLibero) ? arcoLibero : null;

    const coloreDi = useCallback((codice) => {
        const t = perCodice.get(codice);
        if (!t) return COLORI.spento;
        if (t.confermataIl) return COLORI.confermata;
        return COLORI[t.stato] || COLORI.spento;
    }, [perCodice]);

    const scegli = (codice) => {
        setModifica(null);
        setArcoAcceso(null);
        setSel(codice);
        setFitKey((k) => k + 1);
    };

    /* Arco senza tratta scelto nell'elenco: la mappa lo inquadra e lo accende. La tratta
       scelta si lascia (con una tratta scelta gli archi liberi sarebbero spenti in mappa),
       ma non una modifica in corso: e' li' che l'arco si aggiunge alla tratta. */
    const scegliLibero = (a) => {
        if (!modifica) setSel(null);
        setArcoLibero(a.id);
        setInquadra([a.id]);
    };

    // Dal menu dello stato o dal riquadro "Archi senza tratta"; stessa regola di scegliLibero.
    const cambiaStato = (valore) => {
        setFStato(valore);
        setArcoLibero(null);
        if (valore === "liberi" && !modifica) setSel(null);
    };

    // Archi della tratta scelta (o quelli in modifica).
    const selezione = useMemo(() => {
        if (modifica) return modifica;
        return trattaSel ? new Set(trattaSel.archi) : null;
    }, [modifica, trattaSel]);
    /* Archi della tratta com'erano prima della modifica: servono alla mappa per disegnare
       quelli tolti e non ancora salvati. Memo: un Set nuovo a ogni render (anche a ogni
       passaggio del mouse sugli archi) farebbe ricolorare alla mappa tutti i ~3000 archi. */
    const inModifica = !!modifica;
    const originali = useMemo(() => (inModifica && trattaSel ? new Set(trattaSel.archi) : null), [inModifica, trattaSel]);
    const tolti = modifica && trattaSel ? trattaSel.archi.filter((id) => !modifica.has(id)) : [];
    const metriModifica = modifica ? Math.round([...modifica].reduce((s, id) => s + (perId.get(id)?.lunghezza || 0), 0)) : null;
    const presiDaAltre = modifica ? [...modifica].filter((id) => perId.get(id)?.tratta && perId.get(id).tratta !== sel) : [];

    const clicArco = (a) => {
        if (modifica) {
            const s = new Set(modifica);
            if (s.has(a.id)) s.delete(a.id); else s.add(a.id);
            setModifica(s);
        } else if (a.tratta) {
            scegli(a.tratta);
        } else if (vistaLiberi) {
            setArcoLibero(a.id);   // arco senza tratta: si accende anche nell'elenco
        }
    };

    const salvaArchi = () => esegui("Salvataggio", async () => {
        await axios.put(`${BASE}/tratte/${encodeURIComponent(sel)}/archi`, { archi: [...modifica] }, { headers: authHeaders(), params: { zona } });
        setModifica(null);
    });
    const inverti = () => esegui("Inversione", () => axios.post(`${BASE}/tratte/${encodeURIComponent(sel)}/inverti`, {}, { headers: authHeaders(), params: { zona } }));
    const ripristina = () => esegui("Ripristino", () => axios.post(`${BASE}/tratte/${encodeURIComponent(sel)}/ripristina`, {}, { headers: authHeaders(), params: { zona } }));
    const conferma = (codici, soloOk = false) => esegui("Conferma", () => axios.post(`${BASE}/conferma`, { codici, soloOk }, { headers: authHeaders(), params: { zona } }));
    const annullaConferma = () => esegui("Annullamento", () => axios.delete(`${BASE}/tratte/${encodeURIComponent(sel)}/conferma`, { headers: authHeaders(), params: { zona } }));
    const ricalcola = () => esegui("Ricalcolo", () => axios.post(`${BASE}/ricalcola`, {}, { headers: authHeaders(), params: { zona } }));

    // Dopo una conferma singola si passa da soli alla prossima tratta dell'elenco.
    const confermaEProssima = async () => {
        const i = filtrate.findIndex((t) => t.codice === sel);
        const prossima = filtrate.slice(i + 1).find((t) => !t.confermataIl) || null;
        await conferma([sel]);
        if (prossima) scegli(prossima.codice);
    };

    const okDaConfermare = filtrate.filter((t) => t.stato === "ok" && !t.confermataIl).map((t) => t.codice);

    /* Export Excel delle tratte come filtrate qui sopra. Si mandano i codici, non le
       righe: il backend rilegge stato e codici archi di ogni tratta. Stesso schema di
       TabRecuperi (POST + blob + saveAs, nome file dall'header). */
    const esporta = async () => {
        const filtri = [
            FILTRI_STATO.find((f) => f.value === fStato)?.label,
            fGiorno || "tutti i percorsi",
            cerca.trim() && `ricerca "${cerca.trim()}"`,
        ].filter(Boolean).join(" · ");
        try {
            const res = await axios.post(
                `${BASE}/export`,
                { codici: filtrate.map((t) => t.codice), percorso: fGiorno || null, filtri },
                { headers: authHeaders(), params: { zona }, responseType: "blob" }
            );
            const nome = res.headers["content-disposition"]?.match(/filename=([^;]+)/)?.[1]?.trim();
            saveAs(res.data, nome || "geometrie_tratte.xlsx");
        } catch (e) {
            console.error("Errore export Excel:", e);
            alert("Non è stato possibile generare il file Excel.");
        }
    };

    if (loading) return <Lf23Loader testo="Caricamento geometrie…" />;

    if (!dati?.importKey) {
        return (
            <div className="gp-rq-vuoto">
                <strong>Nessun file di archi QGIS caricato.</strong>
                <span>Le geometrie delle tratte si ricavano dagli archi QGIS del cliente: il file va importato una volta (script import_archi_qgis.js), poi qui si validano.</span>
                {errore && <span>{errore}</span>}
            </div>
        );
    }

    return (
        <div className="gp-geo">
            <div className="stats-grid">
                <KpiCard tone="amber" icona={ico(<><path d="M12 9v4" /><path d="M12 17h.01" /><circle cx="12" cy="12" r="10" /></>)} label="Da verificare" valore={nf(conteggi.da_verificare)} sub={` / ${nf(tratte.length)}`} titolo="Un controllo non torna: il motivo e' scritto nella tratta" />
                <KpiCard tone="green" icona={ico(<path d="M20 6 9 17l-5-5" />)} label="OK da confermare" valore={nf(conteggi.ok)} titolo="Tutti i controlli passati: manca solo la conferma" />
                <KpiCard tone="blue" icona={ico(<><path d="M9 12l2 2 4-4" /><path d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6z" /></>)} label="Confermate" valore={nf(conteggi.confermate)} />
                {/* Cliccabile (come "Da chiudere" nello storico): porta al filtro che li elenca. */}
                <div className="gp-kpi-link" role="button" tabIndex={0}
                    onClick={() => cambiaStato("liberi")}
                    onKeyDown={(e) => { if (e.key === "Enter") cambiaStato("liberi"); }}>
                    <KpiCard tone="slate" icona={ico(<><path d="M3 12h18" /><path d="M3 6h18" /><path d="M3 18h18" /></>)} label="Archi senza tratta" valore={nf(dati.archiLiberi)} sub={` / ${nf(dati.archiTotali)}`} titolo="Archi del file che nessuna tratta ha preso, in rosso tratteggiato sulla mappa: clicca per elencarli" />
                </div>
            </div>

            {errore && <div className="gp-rep-avviso"><span>{errore}</span></div>}

            <div className="toolbar">
                <div className="search-box gp-geo-cerca">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
                    <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder={vistaLiberi ? "Cerca codice arco o via…" : "Cerca codice, via o tratto…"} />
                </div>
                <select className="gp-geo-select" value={fStato} onChange={(e) => cambiaStato(e.target.value)}>
                    {FILTRI_STATO.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
                <select className="gp-geo-select" value={fGiorno} onChange={(e) => setFGiorno(e.target.value)}>
                    <option value="">Tutti i percorsi</option>
                    {giorni.map((g) => <option key={g} value={g}>{g}</option>)}
                </select>
                <span className="results-badge">{vistaLiberi ? `${nf(liberi.length)} archi` : `${nf(filtrate.length)} tratte`}</span>
                <span className="spacer" />
                {filtrate.length > 0 && <ExportBtn titolo="Esporta in Excel le tratte dell'elenco, con stato e codici degli archi" onExport={esporta} />}
                {!vistaLiberi && (confermaBlocco ? (
                    <span className="gp-geo-conferma-blocco">
                        Confermare {nf(okDaConfermare.length)} tratte OK dell'elenco?
                        <button className="btn btn-primary gp-new-btn" disabled={!!lavoro} onClick={async () => { setConfermaBlocco(false); await conferma(okDaConfermare, true); }}>Sì, conferma</button>
                        <button className="btn btn-ghost gp-new-btn" onClick={() => setConfermaBlocco(false)}>Annulla</button>
                    </span>
                ) : (
                    <button className="btn btn-ghost gp-new-btn" disabled={!okDaConfermare.length || !!lavoro} onClick={() => setConfermaBlocco(true)}
                        title="Conferma in un colpo le tratte dell'elenco che hanno passato tutti i controlli">
                        Conferma le OK ({nf(okDaConfermare.length)})
                    </button>
                ))}
                <button className="btn btn-ghost gp-new-btn" disabled={!!lavoro} onClick={ricalcola}
                    title="Rifà l'abbinamento automatico. Archi scelti a mano e tratte confermate non cambiano.">
                    {lavoro === "Ricalcolo" ? "Ricalcolo…" : "Ricalcola"}
                </button>
            </div>

            <div className="gp-geo-corpo">
                <div className="gp-geo-elenco">
                    {vistaLiberi && <ElencoArchiLiberi archi={liberi} scelto={arcoLiberoSel} onScegli={scegliLibero} />}
                    {!vistaLiberi && filtrate.length === 0 && <div className="gp-geo-vuoto">Nessuna tratta con questi filtri.</div>}
                    {filtrate.map((t) => (
                        <button key={t.codice} className={`gp-geo-riga ${t.codice === sel ? "attiva" : ""}`} onClick={() => scegli(t.codice)}>
                            <div className="gp-geo-riga-testa">
                                <span className="gp-geo-via">{t.strada}</span>
                                <BadgeStato t={t} />
                            </div>
                            <div className="gp-geo-riga-sub">{t.tratto || "—"}</div>
                            <div className="gp-geo-riga-sub">
                                {t.codice} · {t.percorsi.join(", ")} · {t.metri != null ? `${nf(t.metri)} m` : "—"} su {nf(t.metriRegistro)} m{scartoTxt(t) && ` (${scartoTxt(t)})`}
                                {t.archi.length > 0 && ` · ${nf(t.archi.length)} ${t.archi.length === 1 ? "arco" : "archi"}`}
                                {t.metodo === "manuale" && <span className="gp-geo-manuale"> · a mano</span>}
                            </div>
                            {!t.confermataIl && t.motivi.length > 0 && <div className="gp-geo-motivo">{t.motivi[0]}{t.motivi.length > 1 ? ` (+${t.motivi.length - 1})` : ""}</div>}
                        </button>
                    ))}
                </div>

                <div className="gp-geo-destra">
                    {trattaSel && (
                        <div className="gp-geo-dettaglio">
                            <div className="gp-geo-riga-testa">
                                <div>
                                    <div className="gp-geo-via">{trattaSel.strada} <span className="gp-geo-codice">{trattaSel.codice}</span></div>
                                    <div className="gp-geo-riga-sub">{trattaSel.tratto} · {trattaSel.percorsi.join(", ")}</div>
                                </div>
                                <BadgeStato t={trattaSel} />
                            </div>
                            {modifica ? (
                                <div className="gp-geo-modifica">
                                    <p>Clicca gli archi sulla mappa per aggiungerli o toglierli. Contorno blu: archi tolti da questa tratta, non ancora salvati (si rimettono cliccandoli). Grigi: archi degli stessi giorni già di un'altra tratta; rossi tratteggiati: archi senza tratta.</p>
                                    <p><strong>{nf(metriModifica)} m</strong> selezionati su {nf(trattaSel.metriRegistro)} m del registro.
                                        {presiDaAltre.length > 0 && <> {nf(presiDaAltre.length)} archi verranno tolti ad altre tratte, che andranno ricontrollate.</>}
                                    </p>
                                    {tolti.length > 0 && (
                                        <p className="gp-geo-tolti">
                                            Tolti ({nf(tolti.length)}):{" "}
                                            {(() => {
                                                const codici = tolti.map((id) => perId.get(id)?.codarco).filter(Boolean);
                                                const senza = tolti.length - codici.length;
                                                return [...codici, senza > 0 && `${nf(senza)} senza codice`].filter(Boolean).join(", ");
                                            })()}
                                        </p>
                                    )}
                                    <div className="gp-geo-azioni">
                                        <button className="btn btn-primary gp-new-btn" disabled={!!lavoro} onClick={salvaArchi}>{lavoro === "Salvataggio" ? "Salvataggio…" : "Salva archi"}</button>
                                        <button className="btn btn-ghost gp-new-btn" disabled={!!lavoro} onClick={() => setModifica(null)}>Annulla</button>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {trattaSel.motivi.length > 0 && (
                                        <ul className="gp-geo-motivi">{trattaSel.motivi.map((m) => <li key={m}>{m}</li>)}</ul>
                                    )}
                                    {trattaSel.avvisi.length > 0 && (
                                        <ul className="gp-geo-avvisi">{trattaSel.avvisi.map((m) => <li key={m}>{m}</li>)}</ul>
                                    )}
                                    {trattaSel.archi.length > 0 && (
                                        <div className="gp-geo-archi">
                                            <div className="gp-geo-riga-sub">Archi ({nf(trattaSel.archi.length)}), da inizio a fine:</div>
                                            <div className="gp-geo-chips">
                                                {trattaSel.archi.map((id) => {
                                                    const a = perId.get(id);
                                                    if (!a) return null;
                                                    return (
                                                        <span key={id} className={`gp-geo-chip ${a.codarco ? "" : "senza"} ${arcoAcceso === id ? "accesa" : ""}`}
                                                            title={a.codarco ? `Arco ${a.codarco}` : "Arco senza codice nel file QGIS"}
                                                            onMouseEnter={() => setArcoAcceso(id)} onMouseLeave={() => setArcoAcceso(null)}>
                                                            {a.codarco ? <span className="gp-geo-chip-cod">{a.codarco}</span> : "senza codice"}
                                                            <span className="gp-geo-chip-m">{nf(Math.round(a.lunghezza))} m</span>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}
                                    <div className="gp-geo-azioni">
                                        {trattaSel.confermataIl ? (
                                            <button className="btn btn-ghost gp-new-btn" disabled={!!lavoro} onClick={annullaConferma}>Annulla conferma</button>
                                        ) : (
                                            <button className="btn btn-primary gp-new-btn" disabled={!!lavoro || trattaSel.stato === "non_abbinata"} onClick={confermaEProssima}
                                                title={trattaSel.stato === "da_verificare" ? "Hai controllato sulla mappa e il disegno è giusto nonostante l'avviso" : "Il disegno è giusto"}>
                                                {lavoro === "Conferma" ? "Conferma…" : "Conferma"}
                                            </button>
                                        )}
                                        <button className="btn btn-ghost gp-new-btn" disabled={!!lavoro || !trattaSel.inizio} onClick={inverti} title="Scambia inizio e fine">Inverti inizio/fine</button>
                                        <button className="btn btn-ghost gp-new-btn" disabled={!!lavoro} onClick={() => setModifica(new Set(trattaSel.archi))}>Modifica archi</button>
                                        {trattaSel.metodo === "manuale" && (
                                            <button className="btn btn-ghost gp-new-btn" disabled={!!lavoro} onClick={ripristina} title="Scarta le scelte fatte a mano e torna all'abbinamento automatico">Ripristina automatico</button>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    {!trattaSel && (
                        <div className="gp-geo-legenda">
                            <span><i style={{ background: COLORI.da_verificare }} />Da verificare</span>
                            <span><i style={{ background: COLORI.ok }} />OK</span>
                            <span><i style={{ background: COLORI.confermata }} />Confermata</span>
                            <span><i className="tratteggio" />Senza tratta</span>
                            <span><i className="giunto" />Giunto fra due archi (da vicino)</span>
                            <span className="gp-geo-legenda-nota">
                                {vistaLiberi
                                    ? "Per dare un arco a una tratta: clicca la tratta sulla mappa, poi Modifica archi e clicca l'arco rosso."
                                    : "Clicca una tratta in elenco o un arco sulla mappa."}
                            </span>
                        </div>
                    )}
                    <MappaArchiQgis
                        archi={archi}
                        tratte={perCodice}
                        coloreDi={coloreDi}
                        selezione={selezione}
                        originali={originali}
                        giorniSel={trattaSel?.percorsi}
                        inizio={modifica ? null : trattaSel?.inizio}
                        fine={modifica ? null : trattaSel?.fine}
                        modifica={!!modifica}
                        onClickArco={clicArco}
                        evidenziato={arcoAcceso ?? arcoLiberoSel}
                        onHoverArco={setArcoAcceso}
                        fitKey={fitKey}
                        inquadra={inquadra}
                    />
                </div>
            </div>
        </div>
    );
};

export default TabGeometrieTratte;
